import { getLocalName } from 'vet-bones/utils';

import {
  MappingIdentifier,
  MappingIdentifierType,
} from 'src/containers/ProjectDiagram/types/data';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';

export const getFileUploadAttributeBinding = (
  fieldValue: string,
  rangeIri: string,
  fieldVariable: string
) => {
  // the `sourceField` function returns of the name of the source field referred to by its
  // string argument (especially important because headers might use periods or other symbols)
  return `BIND(StrDt(sourceField("${escapeDoubleQuotes(
    fieldValue
  )}"), <${rangeIri}>) AS ${fieldVariable})`;
};

export const getVirtualGraphAttributeBinding = (
  fieldLabel: string,
  rangeIri: string,
  fieldVariable: string
) => {
  return `BIND(StrDt(?${fieldLabel}, <${rangeIri}>) AS ${fieldVariable})`;
};

export const getFileUploadLabelBinding = (
  rangeIri: string,
  mappingLabelValue: string,
  labelVariable: string
) => {
  return `BIND(<${rangeIri}>(sourceField("${escapeDoubleQuotes(
    mappingLabelValue
  )}")) AS ${labelVariable})`;
};

/**
 * Returns a curried function that returns a unique variable for
 * a class iri, and a binding for the SMS file if the unique variable did not previous exist
 * (in order to avoid duplicate bindings for the same class)
 */
export const getBindingAndVariableFunction = ({
  aliasesByColumnName,
  dataNamespace,
  getStringifiedLabel,
  getUniqueVariable,
}: {
  aliasesByColumnName?: Record<string, string> | null;
  dataNamespace: string;
  getStringifiedLabel: (iri: string) => string;
  getUniqueVariable: (identifier: string) => string;
}) => {
  const getAliasOrOriginalValue = (value: string) =>
    aliasesByColumnName?.[value] || value;
  const classVariablesByIriAndIdentifier: Record<string, string> = {};
  return (classIri: string, identifier: MappingIdentifier) => {
    const key = `${classIri}-${identifier.baseIri}-${identifier.parts
      .map((part) =>
        part.type === MappingIdentifierType.FIELD
          ? getAliasOrOriginalValue(part.value)
          : part.value
      )
      .join('-')}`;

    if (classVariablesByIriAndIdentifier[key]) {
      return {
        binding: null,
        classIri,
        identifierVariable: classVariablesByIriAndIdentifier[key],
      };
    }

    const classLabel = getStringifiedLabel(classIri);
    const compositeIdentifierString = identifier.parts.reduce<string>(
      (acc, part) =>
        part.type === MappingIdentifierType.DELIMITER
          ? `${acc}${part.value}`
          : `${acc}{${getAliasOrOriginalValue(
              escapeDoubleQuotes(part.value)
            )}}`,
      ''
    );
    const identifierVariable = getUniqueVariable(`?${classLabel}_iri`);
    // eslint-disable-next-line no-param-reassign
    classVariablesByIriAndIdentifier[key] = identifierVariable;

    let baseIri: string;
    let isDefaultingToClassIri = false;
    if (identifier.baseIri === null) {
      baseIri = dataNamespace
        ? `${dataNamespace}${getLocalName(classIri)}:`
        : '';
      if (baseIri === '') {
        isDefaultingToClassIri = true;
      }
    } else if (identifier.baseIri === '') {
      baseIri = '';
    } else {
      baseIri = identifier.baseIri;
    }

    if (
      !identifier.parts.some(
        (part) => part.type === MappingIdentifierType.FIELD
      )
    ) {
      return {
        binding: null,
        identifierVariable: `<${
          isDefaultingToClassIri ? `${classIri}-` : baseIri
        }${identifier.parts.map(({ value }) => value).join('')}>`,
      };
    }

    let binding: string;
    if (isDefaultingToClassIri) {
      binding = `BIND(TEMPLATE("${classIri}-${compositeIdentifierString}") AS ${identifierVariable})`;
    } else if (
      baseIri === '' &&
      identifier.parts.length === 1 &&
      identifier.parts[0].type === MappingIdentifierType.FIELD
    ) {
      binding = `BIND(IRI(?${getAliasOrOriginalValue(
        escapeDoubleQuotes(identifier.parts[0].value)
      )}) AS ${identifierVariable})`;
    } else {
      binding = `BIND(TEMPLATE("${baseIri}${compositeIdentifierString}") AS ${identifierVariable})`;
    }

    return { binding, identifierVariable };
  };
};
